import React from "react";

const INK = "#1F1A1A";
const SKIN = "#F6C8A2";
const MOUSTACHE = "#5A3020";
const BILL = "#9CDA97";
const BILL_INK = "#2E6B2E";
// The bills he flicks off the stack, one after another.
const FLICKED_BILLS = ["0s", ".56s", "1.12s", "1.68s"];

/**
 * The chef counting the money: he licks his finger, flicks the bills off the stack, then
 * looks up, winks and says thank you. One 7s loop; with reduced motion he just smiles.
 */
const ChefThanks = ({ t }) => (
  <div role="img" aria-label={t("thanksChefLabel")} className="relative w-[350px] max-w-full h-[196px] flex-none">
    <div aria-hidden="true" className="absolute inset-x-5 bottom-0 h-[26px] rounded-[14px] bg-secondary" />

    <svg
      aria-hidden="true"
      width="200"
      height="190"
      viewBox="0 0 200 190"
      className="absolute left-[130px] bottom-2.5 overflow-visible"
    >
      <path d="M40 192 L46 142 Q100 120 154 142 L160 192 Z" fill="#FFFFFF" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M84 130 L116 130 L100 148 Z" fill="rgb(var(--brand))" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx="100" cy="162" r="2.6" fill={INK} />
      <circle cx="100" cy="176" r="2.6" fill={INK} />

      <g className="thanks-head">
        <circle cx="100" cy="84" r="40" fill={SKIN} stroke={INK} strokeWidth="3" />
        <circle cx="78" cy="30" r="20" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
        <circle cx="122" cy="30" r="20" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
        <circle cx="100" cy="21" r="24" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
        <rect x="66" y="38" width="68" height="22" rx="7" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
        <g className="thanks-brow">
          <path d="M80 66 Q88 61 96 66" fill="none" stroke={MOUSTACHE} strokeWidth="3" strokeLinecap="round" />
          <path d="M104 66 Q112 61 120 66" fill="none" stroke={MOUSTACHE} strokeWidth="3" strokeLinecap="round" />
        </g>
        <g>
          <circle cx="88" cy="80" r="7" fill="#FFFFFF" stroke={INK} strokeWidth="2" />
          <g className="thanks-pupil">
            <circle cx="88" cy="80" r="3.6" fill={INK} />
          </g>
        </g>
        <g className="thanks-wink">
          <circle cx="112" cy="80" r="7" fill="#FFFFFF" stroke={INK} strokeWidth="2" />
          <g className="thanks-pupil">
            <circle cx="112" cy="80" r="3.6" fill={INK} />
          </g>
        </g>
        <circle cx="74" cy="96" r="6" fill="#F48A97" opacity="0.6" />
        <circle cx="126" cy="96" r="6" fill="#F48A97" opacity="0.6" />
        <path className="thanks-smile" d="M92 112 Q100 119 108 112" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
        <path
          className="thanks-grin"
          d="M89 110 Q100 110 111 110 Q109 123 100 123 Q91 123 89 110 Z"
          fill="#FFFFFF"
          stroke={INK}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <ellipse className="thanks-tongue" cx="100" cy="117" rx="5.5" ry="5" fill="#F27A8A" stroke={INK} strokeWidth="1.8" />
        <path d="M83 104 Q92 95 100 102 Q108 95 117 104 Q108 110 100 106 Q92 110 83 104 Z" fill={MOUSTACHE} />
        <path
          className="thanks-spark"
          d="M136 58 L139 65 L146 68 L139 71 L136 78 L133 71 L126 68 L133 65 Z"
          fill="#FFC93C"
          stroke={INK}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </g>

      <g>
        <rect x="44" y="140" width="48" height="26" rx="4" fill="#7FC77A" stroke={BILL_INK} strokeWidth="2.2" transform="rotate(-8 68 153)" />
        <rect x="46" y="136" width="48" height="26" rx="4" fill="#8FD18A" stroke={BILL_INK} strokeWidth="2.2" transform="rotate(-12 70 149)" />
        <g transform="rotate(-12 70 145)">
          <rect x="46" y="132" width="48" height="26" rx="4" fill={BILL} stroke={BILL_INK} strokeWidth="2.2" />
          <circle cx="70" cy="145" r="7" fill="none" stroke={BILL_INK} strokeWidth="1.8" />
          <text x="70" y="149" textAnchor="middle" fontFamily="Rubik, sans-serif" fontSize="11" fontWeight="700" fill={BILL_INK}>
            ₪
          </text>
        </g>
        <circle cx="50" cy="168" r="11" fill={SKIN} stroke={INK} strokeWidth="2.5" />
      </g>

      {FLICKED_BILLS.map((delay) => (
        <rect
          key={delay}
          className="thanks-bill"
          x="46"
          y="132"
          width="48"
          height="26"
          rx="4"
          fill={BILL}
          stroke={BILL_INK}
          strokeWidth="2.2"
          style={{ animationDelay: delay }}
        />
      ))}

      <g className="thanks-hand">
        <circle cx="112" cy="160" r="12" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
        <rect x="93" y="137" width="7" height="14" rx="3.5" fill={SKIN} stroke={INK} strokeWidth="2.2" />
        <circle cx="98" cy="153" r="10" fill={SKIN} stroke={INK} strokeWidth="2.5" />
      </g>
    </svg>

    <div
      aria-hidden="true"
      className="thanks-bubble absolute left-2 top-3.5 px-4 py-3 rounded-[20px_20px_6px_20px] bg-white shadow-toast flex flex-col gap-0.5 whitespace-nowrap"
    >
      <span className="text-xl leading-7 font-bold text-primary">{t("thanksBubbleTitle")}</span>
      <span className="text-xs text-muted">{t("thanksBubbleLine")}</span>
    </div>
  </div>
);

export default ChefThanks;
